import React, { useState, useEffect } from 'react';
import { Carousel, Form, Button } from 'react-bootstrap';
import { Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './RoomDetail.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authcontext';
import api from '../../utils/axiosconfig';
import LoginModal from './LoginModal'
import { toast } from 'react-toastify';

import logoIcon from '../../assets/images/logo_header3.png';
import roomImg1 from '../../assets/images/realImages/room1.jpg';
import roomImg2 from '../../assets/images/realImages/room2.jpg';
import roomImg3 from '../../assets/images/realImages/room3.jpg';
import roomImg4 from '../../assets/images/realImages/room4.jpg';
import roomImg5 from '../../assets/images/realImages/room5.jpg';
import roomImg6 from '../../assets/images/realImages/room6.jpg';
import roomImg7 from '../../assets/images/realImages/room7.jpg';

const RoomDetail = () => {
  const [index, setIndex] = useState(0);
  const [baseRoomPrice, setBaseRoomPrice] = useState(null);
  const [totalPrice, setTotalPrice] = useState(null);
  const [room, setSelectedRoom] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [errors, setErrors] = useState({});
  const [numberOfRooms, setNumberOfRooms] = useState(1);
  const [numberOfDays, setNumberOfDays] = useState(1);
  const [isAvailable, setIsAvailable] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);


  const navigate = useNavigate();
  const { accessToken, login, bookingInfo, saveBookingInfo } = useAuth();

  const images = [roomImg1, roomImg2, roomImg3, roomImg4, roomImg5, roomImg6, roomImg7];

  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };

  const getMinEndDate = (startDate) => {
    if (!startDate) return getTomorrow();
    const minEnd = new Date(startDate);
    minEnd.setDate(minEnd.getDate() + 1);
    return minEnd;
  };

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  useEffect(() => {
    fetchRoomPrice();
    if (bookingInfo) {
      const bookingStartDate = new Date(bookingInfo.from_date);
      const bookingEndDate = new Date(bookingInfo.to_date);

      // Validate stored dates
      const tomorrow = getTomorrow();
      if (bookingStartDate >= tomorrow) {
        setStartDate(bookingStartDate);
        if (bookingEndDate > bookingStartDate) {
          setEndDate(bookingEndDate);
        }
      }
      setNumberOfRooms(bookingInfo.number_of_rooms);
    }
  }, []);

  useEffect(() => {
    calculateTotalPrice();
  }, [adults, children, baseRoomPrice, startDate, endDate]);

  const fetchRoomPrice = async () => {
    try {
      const response = await api.get('/rooms');
      setBaseRoomPrice(response.data[0].price);
      setSelectedRoom(response.data[0]);
    } catch (error) {
      console.error('Error fetching room price:', error);
    }
  };

  const calculateTotalPrice = () => {
    if (baseRoomPrice && startDate && endDate) {
      const requiredRooms = Math.ceil(adults / 3);
      setNumberOfRooms(requiredRooms);

      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      setNumberOfDays(days);

      const newTotalPrice = baseRoomPrice * requiredRooms * days;
      setTotalPrice(newTotalPrice);
    }
  };

  const handleStartDateChange = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const tomorrow = getTomorrow();

    if (selectedDate < tomorrow) {
      return; // Don't allow dates before tomorrow
    }

    setStartDate(selectedDate);

    // If end date exists and is not valid with new start date, clear it
    if (endDate && endDate <= selectedDate) {
      setEndDate(null);
    }

    setIsAvailable(null);
  };

  const handleEndDateChange = (date) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (!startDate) {
      return; // Don't allow setting end date before start date
    }

    const minEndDate = getMinEndDate(startDate);

    if (selectedDate < minEndDate) {
      return; // Don't allow end date before minimum end date
    }

    setEndDate(selectedDate);
    setIsAvailable(null);
  };

  const handleAdultsChange = (e) => {
    const value = parseInt(e.target.value);
    setAdults(value);
    validateAdults(value);
    setIsAvailable(null);
  };

  const handleChildrenChange = (e) => {
    const value = parseInt(e.target.value);
    setChildren(value);
  };

  const validateAdults = (adultCount) => {
    if (adultCount < 1) {
      setErrors(prev => ({ ...prev, adults: "There must be at least 1 adult" }));
    } else if (adultCount > 3) {
      setErrors(prev => ({ ...prev, adults: "Maximum 3 adults per room. Additional rooms will be booked automatically." }));
    } else {
      setErrors(prev => ({ ...prev, adults: null }));
    }
  };

  const validateForm = () => {
    let errors = {};
    if (!startDate || !endDate) {
      errors.dates = "Please select both arrival and departure dates";
    } else {
      const tomorrow = getTomorrow();
      if (startDate < tomorrow) {
        errors.dates = "Arrival date must be tomorrow or later";
      } else if (endDate <= startDate) {
        errors.dates = "Departure date must be at least one day after arrival date";
      }
    }
    if (adults < 1) {
      errors.adults = "There must be at least 1 adult";
    }
    if (children < 0) {
      errors.children = "Number of children cannot be negative";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLoginSuccess = (token) => {
    login(token);
  };

  const checkAvailability = async () => {
    if (!accessToken) {
      setShowLoginModal(true);
      return;
    }

    if (validateForm()) {
      try {
        const response = await api.post('/booking/check-availability', {
          from_date: startDate.toISOString().split('T')[0],
          to_date: endDate.toISOString().split('T')[0],
          number_of_rooms: numberOfRooms
        });
        setIsAvailable(response.data.data[room.type]);
        if (response.data.data[room.type]) {
          const bookingInfo = {
            from_date: startDate.toISOString().split('T')[0],
            to_date: endDate.toISOString().split('T')[0],
            number_of_rooms: numberOfRooms,
            room_id: room.id,
            adults,
            children,
            total_price: totalPrice
          };
          saveBookingInfo(bookingInfo);
          navigate("/checkout");
        } else {
          toast.error('Room is not available for the selected dates');
        }
      } catch (error) {
        console.error('Error checking availability:', error);
        // Check if error is due to authentication
        if (error.response?.status === 401) {
          setShowLoginModal(true);
        } else {
          setIsAvailable(false);
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkAvailability();
  };

  return (
    <>
      <section className="section-sub-banner bg-16">
        <div className="awe-overlay"></div>
        <div className="sub-banner">
          <div className="container">
            <div className="text text-center">
              <h2>AC ROOM</h2>
            </div>
          </div>
        </div>
      </section>
      <div className="room-detail-container">
        <div className="room-detail-content">
          <div className="carousel-container">
            <Carousel activeIndex={index} onSelect={handleSelect} indicators={false}>
              {images.map((image, idx) => (
                <Carousel.Item key={idx}>
                  <img className="d-block w-100" src={image} alt={`Room view ${idx + 1}`} />
                </Carousel.Item>
              ))}
            </Carousel>
            <div className="thumbnail-container">
              {images.map((image, idx) => (
                <img
                  key={idx}
                  src={image}
                  alt={`Thumbnail ${idx + 1}`}
                  className={`thumbnail ${idx === index ? 'active' : ''}`}
                  onClick={() => setIndex(idx)}
                />
              ))}
            </div>
          </div>
          <div className="booking-form">
            <img src={logoIcon} alt="Logo" className="logo" />
            <h6 className="starting-from">STARTING ROOM FROM</h6>
            <p className="price">
              ₹ {totalPrice ? totalPrice.toLocaleString() : room?.price.toLocaleString()}
              <span className="per-day">+ taxes for {numberOfRooms} room(s) for {numberOfDays} night(s)</span>
            </p>

            <Form onSubmit={handleSubmit}>
              <Form.Group>
                <Form.Label>ARRIVE</Form.Label>
                <div className="input-with-icon">
                  <Calendar size={20} />
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    minDate={getTomorrow()}
                    placeholderText="Select arrival date"
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </Form.Group>
              <Form.Group style={{ marginTop: "20px" }}>
                <Form.Label>DEPARTURE</Form.Label>
                <div className="input-with-icon">
                  <Calendar size={20} />
                  <DatePicker
                    selected={endDate}
                    onChange={handleEndDateChange}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate ? getMinEndDate(startDate) : getTomorrow()}
                    placeholderText="Select departure date"
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    disabled={!startDate}
                  />
                </div>
              </Form.Group>
              {errors.dates && <div className="text-danger">{errors.dates}</div>}
              <Form.Group style={{ marginTop: "20px" }}>
                <Form.Label>ADULT</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={adults}
                  onChange={handleAdultsChange}
                />
                {errors.adults && <div className="text-danger">{errors.adults}</div>}
              </Form.Group>
              <Form.Group style={{ marginTop: "20px" }}>
                <div>CHILDREN</div>
                <div style={{ color: "#808080" }}>0 to 5 years old</div>
                <Form.Control
                  type="number"
                  min={0}
                  value={children}
                  onChange={handleChildrenChange}
                />
                {errors.children && <div className="text-danger">{errors.children}</div>}
              </Form.Group>
              <Button className="submit-btn" type="submit" block>CHECK AVAILABILITY</Button>

              {isAvailable !== null && (
                <div className={`availability-message ${isAvailable ? 'text-success' : 'text-danger'}`}>
                  {!isAvailable && 'Room is not available for the selected dates.'}
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>

      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default RoomDetail;